import type { ComponentChildren } from 'preact';

/**
 * Shared filter primitives - extracted from emporium's CatalogFilters.
 */

const ROW_BASE =
  'flex w-full cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] transition-colors';
const ROW_INACTIVE = 'text-fg-primary hover:bg-surface-raised';
const ROW_ACTIVE = 'bg-accent-glow text-fg-primary';

/**
 * Clickable filter row.
 */
export function FilterRow({
  active,
  onToggle,
  leading,
  count,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  leading?: ComponentChildren;
  count?: number | string;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      class={`${ROW_BASE} ${active ? ROW_ACTIVE : ROW_INACTIVE}`}
    >
      {leading}
      <span class="flex-1 truncate">{children}</span>
      {count !== undefined && (
        <span
          class={`font-mono text-[10.5px] [font-feature-settings:'tnum'] ${
            active ? 'text-accent' : 'text-fg-faint'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * Filter row with a faux-checkbox glyph.
 */
export function ToggleRow({
  active,
  onToggle,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      class={`${ROW_BASE} ${active ? ROW_ACTIVE : ROW_INACTIVE}`}
    >
      <span
        aria-hidden="true"
        class={`inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
          active ? 'border-accent bg-accent' : 'border-rule-strong'
        }`}
      >
        {active && (
          <svg width="8" height="6" viewBox="0 0 8 6" aria-hidden="true" class="text-surface-base">
            <path
              d="M1 3 L3 5 L7 1"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        )}
      </span>
      <span class="flex-1">{children}</span>
    </button>
  );
}

interface ChipOption<T extends string> {
  id: T;
  label: string;
}

/**
 * Chip group - rounded-full pill buttons that fill with accent when active.
 */
export function ChipGroup<T extends string>({
  options,
  selected,
  onToggle,
  ariaLabel,
}: {
  options: ChipOption<T>[];
  selected: Set<T>;
  onToggle: (id: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div class="flex flex-wrap gap-1.5" role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = selected.has(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            aria-pressed={active}
            class={`rounded-full border px-3 py-1 text-[12px] transition-colors ${
              active
                ? 'border-accent bg-accent text-surface-base'
                : 'border-rule-strong text-fg-muted hover:border-fg-muted hover:text-fg-primary'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
